import { useEffect, useSyncExternalStore } from "react";

/**
 * Does the screen under the tour have SAMPLE rows in it?
 *
 * The page is the only thing that knows: it swapped its empty list for
 * fixtures because a blank panel cannot be explained. The launcher needs to
 * know so the tour card can say so — the notice used to sit beside the rows,
 * which put the same sentence twice in one screenful, since the samples exist
 * only while that card is up.
 *
 * Same shape as `page-tour.ts`, and for the same reason: exactly one page is
 * mounted, and it clears on unmount.
 */
let showing = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Declares, for as long as this page is mounted, that it is showing samples. */
export function useDeclareSampleData(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    showing = true;
    emit();
    return () => {
      showing = false;
      emit();
    };
  }, [active]);
}

/** True while the screen under the tour is standing in sample rows. */
export function useShowingSampleData(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => showing,
    () => false,
  );
}
